import { isNil } from "lodash-es";
import { makeAutoObservable } from "mobx";

import { apiClient } from "../common/apiClient";
import { ensureError } from "../common/errors";
import { logger } from "../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../common/platformRoutes";
import type { BaseStoreType } from "../common/types/BaseStoreType";
import type { PowerBIEmbedData, PowerBIPayload } from "../common/types/powerBITypes";
import {
   type AsyncData,
   initializeWithDefaultData,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsWaitingForData
} from "../common/utils/asyncDataUtils";
import theme from "../themes/theme";
import authStore from "./auth/authStore";

export type AvailablePowerBIReports = "keyfigures" | "aretsmatglede" | "aretsmatglede_dagligvare";

type PowerBIStoreType = BaseStoreType & {
   embedData: Partial<Record<AvailablePowerBIReports, AsyncData<PowerBIEmbedData | null>>>;

   getEmbedToken: (reportName: AvailablePowerBIReports) => AsyncData<PowerBIEmbedData | null>;
   generateEmbedToken: (reportName: AvailablePowerBIReports) => Promise<void>;
};

const powerBiStore: PowerBIStoreType = makeAutoObservable({
   embedData: {},

   getEmbedToken: (reportName: AvailablePowerBIReports) => {
      if (!powerBiStore.embedData[reportName]) {
         powerBiStore.embedData[reportName] = initializeWithDefaultData(null);
      }

      return powerBiStore.embedData[reportName];
   },

   generateEmbedToken: async (reportName: AvailablePowerBIReports) => {
      if (!authStore.isLoggedIn() || isNil(authStore.currentCompany)) {
         logger.warn("account", "Not logged in or no company selected, skipping PowerBI token generation.");
         return;
      }

      const entry = powerBiStore.embedData[reportName];
      if (!entry) {
         logger.warn(
            "account",
            "Do not call generateEmbedToken directly, use getEmbedToken to allow store to be initialized properly."
         );
         return;
      }

      const abortController = setAsWaitingForData(entry);

      try {
         const url = resolvePlatformRequestUrl(platformRoutes.powerBIReport, {
            storeId: theme.storeId,
            reportName,
            customerNumber: authStore.currentCompany ?? ""
         });
         const embedData: PowerBIPayload = await apiClient(url, authStore.getSessionToken()).signal(abortController).get().json();

         if (embedData.expiration) {
            const fullEmbedData = {
               ...embedData,
               expirationDate: new Date(embedData.expiration)
            };
            setAsDataAvailable(entry, fullEmbedData);
         } else {
            setAsErrorOccured(entry, "Unknown object returned from server");
         }
      } catch (err) {
         setAsErrorOccured(entry, ensureError(err).message);
      }
   },

   clearCompanySpecificData: () => {
      powerBiStore.embedData = {};
   }
} satisfies PowerBIStoreType);

export default powerBiStore;
